// Passengers & Luggage modal logic (refactored for clarity)
import { gtag_report_conversion_pas_confirm_click } from '../../../gtag-conversion.js';
import { loadLs, saveLs } from '../../../storage/localStorage.js';
import { setTripExtrasOrderData } from '../orderData.js';

const KEY_LS = 'trip-extra';

let modal,
  form,
  prevFocusedEl,
  statusLive,
  errPassengers,
  errLuggage,
  seatsError;
let checkboxSeats, seatRowsContainer;

// -------- Utility getters --------
function getPassengers() {
  return parseInt(form?.passengers?.value, 10) || 0;
}
function getLuggage() {
  return parseInt(form?.luggage?.value, 10) || 0;
}
function getMsg(key) {
  return form?.getAttribute(`data-msg-${key}`) || '';
}
function getTotalSeats() {
  if (!seatRowsContainer || !checkboxSeats?.checked) return 0;
  let total = 0;
  seatRowsContainer.querySelectorAll('[data-seat-count]').forEach(el => {
    total += parseInt(el.textContent.trim(), 10) || 0;
  });
  return total;
}

// -------- DOM / state --------
function cacheRefs() {
  modal = document.getElementById('passengers-modal');
  form = document.getElementById('passengers-form');
  statusLive = document.getElementById('passengers-modal-status');
  errPassengers = document.getElementById('passengers-error');
  errLuggage = document.getElementById('luggage-error');
  seatsError = document.getElementById('seats-error');
  checkboxSeats = modal?.querySelector('#add-child-seats') || null;
  seatRowsContainer = modal?.querySelector('#child-seats-rows') || null;
}

function restoreState() {
  const prev = loadLs(KEY_LS) || {};
  if (prev.passengers) form.passengers.value = prev.passengers;
  if (prev.luggage) form.luggage.value = prev.luggage;
  if (checkboxSeats && seatRowsContainer) {
    checkboxSeats.checked = !!prev.seatsEnabled;
    seatRowsContainer.hidden = !checkboxSeats.checked;
    if (prev.seats) {
      Object.entries(prev.seats).forEach(([type, count]) => {
        const target = seatRowsContainer.querySelector(
          `.seat-row[data-seat-type="${type}"] [data-seat-count]`
        );
        if (target) target.textContent = String(count);
      });
    }
  }
  capSeats();
  // sync visible counters with restored values
  syncCounter('passengers');
  syncCounter('luggage');
}

// -------- Modal open/close --------
export function openPassengersModal() {
  cacheRefs();
  if (!modal || !form) return;
  restoreState();
  // Ensure sensible defaults for counters
  if (getPassengers() <= 0) {
    form.passengers.value = 1;
    syncCounter('passengers');
  }
  if (isNaN(parseInt(form.luggage.value, 10))) {
    form.luggage.value = 0;
    syncCounter('luggage');
  }
  prevFocusedEl = document.activeElement;
  modal.hidden = false;
  document.body.classList.add('no-scroll');
  const focusBtn = modal.querySelector('[data-pt-passengers-plus]');
  if (focusBtn) focusBtn.focus();
  else form.passengers.focus();
  document.addEventListener('keydown', onKeydown);
}

function closePassengersModal() {
  if (!modal) return;
  modal.hidden = true;
  document.body.classList.remove('no-scroll');
  document.removeEventListener('keydown', onKeydown);
  if (prevFocusedEl?.focus) prevFocusedEl.focus();
}

// -------- Validation helpers --------
function showError(el, text) {
  if (el) el.textContent = text || '';
}
function clearError(el) {
  if (el) el.textContent = '';
}
function announce(text) {
  if (statusLive) statusLive.textContent = text || '';
}

function validateInputs() {
  let ok = true;
  const p = getPassengers();
  const l = getLuggage();
  if (p <= 0) {
    form.passengers.classList.add('error');
    showError(errPassengers, getMsg('error-passengers'));
    form.passengers.setAttribute('aria-invalid', 'true');
    ok = false;
  } else {
    form.passengers.classList.remove('error');
    clearError(errPassengers);
    form.passengers.removeAttribute('aria-invalid');
  }
  if (l < 0 || isNaN(l)) {
    form.luggage.classList.add('error');
    showError(errLuggage, getMsg('error-luggage'));
    form.luggage.setAttribute('aria-invalid', 'true');
    ok = false;
  } else {
    form.luggage.classList.remove('error');
    clearError(errLuggage);
    form.luggage.removeAttribute('aria-invalid');
  }
  if (ok && checkboxSeats?.checked) {
    const totalSeats = getTotalSeats();
    if (totalSeats > p) {
      showError(seatsError, getMsg('seats-exceed'));
      ok = false;
    } else {
      clearError(seatsError);
    }
  } else if (seatsError) {
    clearError(seatsError);
  }
  return ok;
}

// -------- Counter (passengers/luggage) helpers --------
function syncCounter(type) {
  if (!form) return;
  const isPassengers = type === 'passengers';
  const input = isPassengers ? form.passengers : form.luggage;
  const span = modal?.querySelector(
    isPassengers ? '[data-pt-passengers-value]' : '[data-pt-luggage-value]'
  );
  if (!input || !span) return;
  const val = parseInt(input.value, 10);
  span.textContent = isNaN(val) ? '0' : String(val);
}

function adjustCounter(type, delta) {
  if (!form) return;
  const isPassengers = type === 'passengers';
  const input = isPassengers ? form.passengers : form.luggage;
  if (!input) return;
  const min = parseInt(input.getAttribute('min') || '0', 10);
  const max = parseInt(input.getAttribute('max') || '9999', 10);
  const cur = parseInt(input.value || '0', 10) || 0;
  const next = Math.max(min, Math.min(max, cur + delta));
  input.value = String(next);
  syncCounter(type);
  if (isPassengers) {
    // passengers changes can affect seats cap
    capSeats();
  }
}

// -------- Seats logic --------
function capSeats() {
  if (!checkboxSeats?.checked) {
    enableAllPlus();
    return;
  }
  const passengers = getPassengers();
  if (passengers <= 0) {
    enableAllPlus();
    return;
  }
  let total = getTotalSeats();
  if (total > passengers) {
    const shrinkOrder = ['booster', 'child', 'infant'];
    let excess = total - passengers;
    shrinkOrder.forEach(type => {
      if (excess <= 0) return;
      const countEl = seatRowsContainer.querySelector(
        `.seat-row[data-seat-type="${type}"] [data-seat-count]`
      );
      if (!countEl) return;
      let count = parseInt(countEl.textContent.trim(), 10) || 0;
      if (count === 0) return;
      const remove = Math.min(count, excess);
      countEl.textContent = String(count - remove);
      excess -= remove;
    });
    announce(getMsg('seats-exceed'));
  }
  // Disable plus if capped
  const capped = getTotalSeats() >= passengers;
  seatRowsContainer.querySelectorAll('[data-seat-plus]').forEach(btn => {
    btn.disabled = capped;
  });
}
function enableAllPlus() {
  seatRowsContainer?.querySelectorAll('[data-seat-plus]')?.forEach(btn => {
    btn.disabled = false;
  });
}
function adjustSeat(row, delta) {
  const countEl = row.querySelector('[data-seat-count]');
  if (!countEl) return;
  let count = parseInt(countEl.textContent.trim(), 10) || 0;
  count = Math.max(0, count + delta);
  countEl.textContent = String(count);
}

// -------- Event bindings --------
function bindEvents() {
  if (!modal || !form) return;
  modal.addEventListener('click', e => {
    if (e.target.dataset.ptClose !== undefined) closePassengersModal();
  });
  // Counter clicks
  modal.addEventListener('click', e => {
    if (e.target.closest('[data-pt-passengers-minus]')) {
      adjustCounter('passengers', -1);
    } else if (e.target.closest('[data-pt-passengers-plus]')) {
      adjustCounter('passengers', 1);
    } else if (e.target.closest('[data-pt-luggage-minus]')) {
      adjustCounter('luggage', -1);
    } else if (e.target.closest('[data-pt-luggage-plus]')) {
      adjustCounter('luggage', 1);
    }
  });
  form.addEventListener('submit', e => {
    e.preventDefault();
    if (!validateInputs()) return;
    // Collect seats
    gtag_report_conversion_pas_confirm_click();

    let seatsEnabled = !!checkboxSeats?.checked;
    const seats = { infant: 0, child: 0, booster: 0 };
    if (seatsEnabled && seatRowsContainer) {
      seatRowsContainer.querySelectorAll('.seat-row').forEach(r => {
        const type = r.getAttribute('data-seat-type');
        const cEl = r.querySelector('[data-seat-count]');
        const c = parseInt(cEl?.textContent.trim(), 10) || 0;
        if (type) seats[type] = c;
      });
    }
    const extras = {
      passengers: getPassengers(),
      luggageUnits: getLuggage(),
      seatsEnabled,
      childSeats: seats,
    };
    // зберігаємо локально і у глобальному orderData
    saveLs(KEY_LS, extras);
    setTripExtrasOrderData(extras);
    announce(getMsg('saved'));
    closePassengersModal();
    window.dispatchEvent(new CustomEvent('passengersModal:confirmed'));
  });
  // Checkbox toggle
  checkboxSeats?.addEventListener('change', () => {
    if (!seatRowsContainer) return;
    if (checkboxSeats.checked && getPassengers() <= 0) {
      form.passengers.value = 1;
      announce(getMsg('seats-passengers-required'));
      syncCounter('passengers');
    }
    seatRowsContainer.hidden = !checkboxSeats.checked;
    checkboxSeats.setAttribute(
      'aria-expanded',
      checkboxSeats.checked ? 'true' : 'false'
    );
    announce(
      checkboxSeats.checked ? getMsg('seats-enabled') : getMsg('seats-disabled')
    );
    capSeats();
  });
  // Seats +/-
  seatRowsContainer?.addEventListener('click', e => {
    const minus = e.target.closest('[data-seat-minus]');
    const plus = e.target.closest('[data-seat-plus]');
    if (!minus && !plus) return;
    if (plus && getPassengers() <= 0) {
      form.passengers.value = 1;
      announce(getMsg('seats-passengers-required'));
    }
    const row = e.target.closest('.seat-row');
    if (!row) return;
    adjustSeat(row, minus ? -1 : 1);
    capSeats();
  });
  // Passengers change
  form.passengers.addEventListener('input', () => {
    syncCounter('passengers');
    capSeats();
  });
  // Luggage change
  form.luggage.addEventListener('input', () => {
    syncCounter('luggage');
  });
}

function initModal() {
  cacheRefs();
  if (!modal || !form) return;
  // initial counters sync on DOM ready
  syncCounter('passengers');
  syncCounter('luggage');
  bindEvents();
}

document.addEventListener('DOMContentLoaded', initModal);

function onKeydown(e) {
  if (e.key === 'Escape' || e.key === 'Esc') {
    e.preventDefault();
    closePassengersModal();
    return;
  }
  if (e.key === 'Tab' && !modal.hidden) {
    trapFocus(e);
  }
}

function trapFocus(e) {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const dialog = modal.querySelector('.pt-modal__dialog');
  const focusables = Array.from(
    dialog.querySelectorAll(focusableSelectors)
  ).filter(el => el.offsetParent !== null);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (e.shiftKey) {
    if (active === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
